import { facebookLogin } from "./AuthService"; // Import from AuthService
import { localData } from "../functions/api/localData";
import { setUserProfile } from "../account/profileData";
import { setSession } from "../utils/auth";

// --- Internal Helper Functions ---

/**
 * Handles the successful login process after obtaining a Facebook access token.
 * Calls the backend login endpoint via AuthService and manages session/profile state.
 * Mirrors the structure of handleGoogleLoginSuccess.
 * @async
 * @param {string} accessToken - Facebook OAuth 2.0 access token.
 * @param {Function} setProcessing - State setter for processing indicator.
 * @param {Function} setReport - State setter for user feedback messages.
 */
const handleFacebookLoginSuccess = async (accessToken, setProcessing, setReport) => {
  try {
    // Call the backend service to validate the token and log the user in
    const response = await facebookLogin(accessToken);

    // Check for a successful response and valid user data
    if (response?.status === 200 && response.data?.user) {
      const { user } = response.data;
      const privacyStatus = await localData("get", "privacy"); // Check privacy consent

      // Proceed only if the user has accepted the privacy policy
      if (privacyStatus === "accept") {
        user.last_refresh = new Date().toISOString(); // Add timestamp for refresh tracking
        await setUserProfile(user); // Persist user profile locally
        setSession(true); // Update application session state to logged in
        setReport({
          show: true,
          message: response.data.message || "Logged in successfully using Facebook!",
          type: "success",
        });
      } else {
        // If privacy not accepted, clear any potential local data and inform the user
        await localData("remove", "privacy");
        localStorage.removeItem("session"); // Ensure no session is stored
        setReport({
          show: true,
          message:
            "Login successful. Please review and accept our Privacy Policy to proceed.",
          type: "info", // Use 'info' type for this message
        });
      }
    } else {
      // Handle unsuccessful backend response (e.g., token invalid, user not found)
      const errorMessage = response?.data?.message || "Facebook login failed on the server.";
      console.error("Backend Facebook Login Failed:", errorMessage, response);
      setReport({ show: true, message: errorMessage, type: "error" });
    }
  } catch (err) {
    // Handle network errors or unexpected issues during the backend call
    console.error("Backend Facebook Login Error:", err);
    // Delegate to the specific error handler, which also sets processing to false
    handleFacebookLoginError(err, setProcessing, setReport);
    // Return early as the finally block will run anyway
    return;
  } finally {
    // Ensure the processing indicator is always turned off, regardless of success or failure
    // Note: handleFacebookLoginError also calls setProcessing(false), but this ensures it happens even if that function isn't reached.
    setProcessing(false);
  }
};

/**
 * Handles errors occurring during the Facebook login flow (SDK or backend communication).
 * Mirrors the structure of handleGoogleLoginError.
 * @param {Error|object} error - The error object.
 * @param {Function} setProcessing - Function to set the processing state.
 * @param {Function} setReport - Function to set user feedback messages.
 */
const handleFacebookLoginError = (error, setProcessing, setReport) => {
  console.error("Facebook Login Failed:", error);
  setReport({
    show: true,
    // Provide a user-friendly error message
    message: error?.message || "Facebook login failed. Please try again.",
    type: "error",
  });
  // Ensure processing indicator is turned off on error
  setProcessing(false);
};

// --- Exported Initiator Function ---

/**
 * Initiates the Facebook Login Flow using the Facebook SDK directly.
 * Assumes the Facebook SDK (window.FB) is loaded.
 * Mirrors the structure of initiateGoogleLoginFlow.
 * @param {Function} setProcessing - State setter for processing indicator.
 * @param {Function} setReport - State setter for user feedback messages.
 * @param {Function} [onSuccessCallback] - Optional callback function to execute with the access token
 *                                         instead of the default login logic. This callback is responsible
 *                                         for its own API calls, reporting, and setting processing state to false.
 */
export const initiateFacebookLoginFlow = (
  setProcessing,
  setReport,
  onSuccessCallback // Add optional callback
) => {
  const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID;

  // Validate environment configuration
  if (!FACEBOOK_APP_ID) {
    console.error("REACT_APP_FACEBOOK_APP_ID is not set.");
    setReport({
      show: true,
      message: "Facebook Login is not configured.",
      type: "error",
    });
    return; // Stop the flow if not configured
  }

  // Check if Facebook SDK is loaded
  if (typeof window.FB === "undefined") {
    console.error("Facebook SDK not loaded.");
    setReport({
      show: true,
      message:
        "Facebook SDK failed to load. Please check your connection or configuration.",
      type: "error",
    });
    return; // Stop the flow if SDK is missing
  }

  setProcessing(true); // Indicate processing start before SDK call
  let callbackCompleted = false; // Flag to prevent double error handling

  // Define the async logic separately
  const handleFbCallback = async (response) => {
    try {
      if (response.authResponse && response.authResponse.accessToken) {
        // User authenticated successfully via Facebook SDK
        console.log("Facebook SDK Login Success:", response);
        if (typeof onSuccessCallback === 'function') {
          // Execute custom callback if provided
          await onSuccessCallback(response.authResponse.accessToken);
          // Assume onSuccessCallback handles setProcessing(false)
        } else {
          // Proceed with default backend verification and application login logic
          await handleFacebookLoginSuccess(
            response.authResponse.accessToken,
            setProcessing, // Pass setProcessing
            setReport
          );
        }
      } else {
        // Log full response for debugging, including potential 2-step auth challenges
        console.warn("Facebook login response:", response);
        setReport({
          show: true,
          message:
            "Facebook login failed (possible two-step authentication challenge or cancellation). Please try again.",
          type: "error",
        });
        throw new Error("Facebook login cancelled or incomplete.");
      }
    } catch (callbackError) {
      console.error("Error within Facebook callback execution:", callbackError);
      if (typeof onSuccessCallback === 'function') {
        // Report error for custom callback flow, assume callback handles setProcessing(false)
        setReport({ show: true, message: callbackError.message || "An error occurred after Facebook authentication.", type: "error" });
      } else {
        // Use the standard error handler for the default flow
        handleFacebookLoginError(callbackError, setProcessing, setReport);
      }
    } finally {
      callbackCompleted = true;
      // Note: setProcessing(false) is handled within handleFacebookLoginSuccess/Error
      // or assumed to be handled by onSuccessCallback
    }
  };

  // Call the Facebook SDK login method with a standard function callback
  window.FB.login(
    function (response) { // Use a standard function here
      // Call the async handler function
      handleFbCallback(response);
    },
    { scope: "public_profile,email" } // Request necessary permissions
  );

  // Note: The FB SDK doesn't have a direct 'error_callback' like Google's token client
  // for scenarios like popup closure before selection. The main callback handles
  // authResponse presence to determine success/cancellation. If the SDK itself fails
  // to initialize or load, the initial checks handle that.
};
